import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { QarepaBaseSubsDirective } from '@qarepa/common';
import { CheckService } from '@qaroni-app/check/services/check.service';
import { CheckData } from '@qaroni-app/check/types/check';
import { CheckActionEnum } from '@qaroni-app/check/types/check-action.enum';
import { CheckHistory } from '@qaroni-app/check/types/check-history';
import { EmployeeCheckTypeEnum } from '@qaroni-app/employee/types/employee-checks-type.enum';
import { ScheduleCheckService } from '@qaroni-app/schedule/services/schedule-check.service';
import { CheckHistoryDialogData } from '@qaroni-shared/types/check-history-dialog-data';

@Component({
  selector: 'qaroni-check-history-dialog',
  templateUrl: './check-history-dialog.component.html',
  styleUrl: './check-history-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CheckHistoryDialogComponent
  extends QarepaBaseSubsDirective
  implements OnInit
{
  data = inject<CheckHistoryDialogData>(MAT_DIALOG_DATA);
  private router = inject(Router);
  private checkService = inject(CheckService);
  private scheduleCheckService = inject(ScheduleCheckService);
  private _cdr = inject(ChangeDetectorRef);

  public check$ = this.checkService.getCheckHistorySubject$();
  public scheduleCheck$ = this.scheduleCheckService.getCheckHistorySubject$();

  public checkHistoryList: CheckHistory[] = [];
  public expandedHistoryId: number | null = null;

  public mapOptions: google.maps.MapOptions = {
    disableDefaultUI: true,
    zoomControl: false,
    scrollwheel: false,
    draggable: false,
    disableDoubleClickZoom: true,
    clickableIcons: false,
  };

  ngOnInit(): void {
    this.subs.add(this.check$.subscribe(this.getCheckHistory));
    this.subs.add(this.scheduleCheck$.subscribe(this.getCheckHistory));

    if (this.data.type === 'company') {
      this.scheduleCheckService.checkHistory(
        this.companyID,
        this.employeeID,
        this.checkID
      );
    } else {
      this.checkService.checkHistory(
        this.companyID,
        this.employeeID,
        this.checkID
      );
    }
  }

  get employeeID() {
    return this.data.employeeID;
  }

  get companyID() {
    return this.data.companyID;
  }

  get checkID() {
    return this.data.checkID;
  }

  get hasHistory() {
    return this.checkHistoryList.length > 0;
  }

  get type() {
    return this.data.type;
  }

  get checkDate() {
    return `${this.data.dayName},  ${this.data.dayNumber} / ${this.data.monthName}`;
  }

  get hasComment() {
    return this.checkHistoryList.some(check => check.comment);
  }

  private getCheckHistory = (checkHistory: CheckHistory[]) => {
    this.checkHistoryList = checkHistory;
    this._cdr.markForCheck();
  };

  public getIconActionType(action: CheckActionEnum) {
    switch (action) {
      case CheckActionEnum.LIVE:
        return 'timer_play';
      case CheckActionEnum.MANUAL:
        return 'border_color';
      default:
        return 'question_mark';
    }
  }

  public getIconDeviceType(
    type: EmployeeCheckTypeEnum | null | undefined
  ): string {
    {
      const upperType = type?.toUpperCase();

      switch (upperType) {
        case EmployeeCheckTypeEnum.ANDROID:
        case EmployeeCheckTypeEnum.IOS:
          return 'smartphone';
        case EmployeeCheckTypeEnum.WEB:
          return 'laptop';
        case EmployeeCheckTypeEnum.IN_PERSON_PIN:
          return 'devices_other';
        default:
          return 'device_unknown';
      }
    }
  }

  public isWorkstationCheck(
    type: EmployeeCheckTypeEnum | null | undefined
  ): boolean {
    return (
      type === EmployeeCheckTypeEnum.IN_PERSON_PIN ||
      type === EmployeeCheckTypeEnum.REMOTE_PIN
    );
  }

  public hasDetails(check: CheckHistory): boolean {
    const hasComment = !!check.comment && check.comment.trim().length > 0;
    const hasIp = !!check?.ip && check.ip.trim().length > 0;
    const hasLocation = this.hasValidLocation(check);

    return hasComment || hasIp || hasLocation;
  }

  public getHistoryData(history: CheckHistory): CheckData | null {
    if (history.check?.data && history.check.data.length > 0) {
      return history.check.data[0];
    }
    return null;
  }

  public toggleExpand(id: number): void {
    if (this.expandedHistoryId === id) {
      this.expandedHistoryId = null;
    } else {
      this.expandedHistoryId = id;
    }
  }

  public hasValidLocation(history: CheckHistory): boolean {
    if (!history.latitude || !history.longitude) {
      return false;
    }

    const lat = parseFloat(String(history.latitude));
    const lng = parseFloat(String(history.longitude));

    return !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
  }

  public getMapPosition(check: CheckHistory): google.maps.LatLngLiteral {
    if (!check) {
      return { lat: 0, lng: 0 };
    }

    return {
      lat: parseFloat(String(check.latitude)),
      lng: parseFloat(String(check.longitude)),
    };
  }
}
